/** Small token-based primitives shared by every screen (prototype `.card`, `.pad`, `.btn`, `.head`). */

import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import { PageHelpButton } from './PageHelp'

export function Card({
  children,
  className,
  padded = true,
  style,
}: {
  children: ReactNode
  className?: string
  padded?: boolean
  style?: CSSProperties
}) {
  return (
    <div className={`card${padded ? ' pad' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'danger'
  size?: 'md' | 'sm'
}

export function Button({ variant = 'default', size = 'md', className, ...rest }: ButtonProps) {
  const classes = [
    'btn',
    variant === 'primary' ? 'pri' : variant === 'danger' ? 'dan' : '',
    size === 'sm' ? 'sm' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')
  return <button type="button" className={classes} {...rest} />
}

export function PageHead({
  title,
  subtitle,
  actions,
  help,
}: {
  title: string
  /** A node, so a screen can emphasise part of its strapline as the prototype does. */
  subtitle?: ReactNode
  actions?: ReactNode
  /** The page's id in `lib/pageHelp.ts` — puts an ⓘ beside the title (video + FAQ). */
  help?: string
}) {
  return (
    <div className="head" style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
      <div style={{ flex: 1 }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          {help ? <PageHelpButton page={help} /> : null}
        </h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {actions ? <div className="row" style={{ gap: 8 }}>{actions}</div> : null}
    </div>
  )
}

/** Loading / empty / error placeholder using the prototype's dashed `.note` panel. */
export function Placeholder({ children }: { children: ReactNode }) {
  return <div className="note">{children}</div>
}
